import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Model, Msg, SystemTag, TagDef, TagValueType } from "../types";
import { formatValue } from "../editor/values";

const ALL_TYPES: TagValueType[] = ["none", "string", "number", "date", "datetime", "duration"];
const TYPE_LABELS: Record<TagValueType, string> = {
  none: "None",
  string: "Text",
  number: "Number",
  date: "Date",
  datetime: "Date & time",
  duration: "Duration",
};

// Whether the draft differs from the saved registry (controls Save/Reset).
function isDirty(model: Model): boolean {
  return JSON.stringify(model.registryDraft) !== JSON.stringify(model.registry);
}

// Reasons the draft cannot be saved (blank/duplicate/reserved names). Closed-
// enum-without-values can't occur — the update layer forbids it.
function validationError(draft: TagDef[], systemNames: Set<string>): string | null {
  const seen = new Set<string>();
  for (const def of draft) {
    const name = def.name.trim();
    if (!name) return "Every tag needs a name.";
    if (systemNames.has(name)) return `'${name}' is a reserved system tag.`;
    if (seen.has(name)) return `Duplicate tag name '${name}'.`;
    seen.add(name);
    // Predefined values must match the tag's type (e.g. after a type change
    // leaves old values behind).
    for (const val of def.values) {
      if (val.type !== def.valueType) {
        return `'${formatValue(val)}' is not a valid ${TYPE_LABELS[def.valueType].toLowerCase()} value for '${name}'.`;
      }
    }
  }
  return null;
}

export function viewTagsPage(model: Model, dispatch: (msg: Msg) => void): VNode {
  const savedNames = new Set(model.registry.map((d) => d.name));
  const systemNames = new Set(model.systemTags.map((d) => d.name));
  const error = validationError(model.registryDraft, systemNames);
  const dirty = isDirty(model);

  return h("section.tags-page", [
    h("div.page__head", [
      h("h1.page__title", "Tags"),
      h(
        "button.page__back",
        { on: { click: () => dispatch({ type: "GoHome" }) } },
        "← Back"
      ),
    ]),
    ...(model.systemTags.length > 0
      ? [
          h("h2.page__subhead", "System tags"),
          h(
            "div.tags-page__system",
            model.systemTags.map((def) => viewSystemTagRow(def))
          ),
          h("h2.page__subhead", "Your tags"),
        ]
      : []),
    h(
      "div.tags-page__list",
      model.registryDraft.length === 0
        ? [h("p.page__empty", "No tags yet. Add one to get started.")]
        : model.registryDraft.map((def, i) => viewTagRow(def, i, savedNames, dispatch))
    ),
    h(
      "button.tags-page__add",
      { on: { click: () => dispatch({ type: "RegistryAddTag" }) } },
      "+ Add tag"
    ),
    ...(error ? [h("p.tags-page__error", error)] : []),
    h("div.tags-page__actions", [
      h(
        "button.tags-page__save",
        {
          attrs: { disabled: !dirty || error !== null },
          on: { click: () => dispatch({ type: "RegistrySave" }) },
        },
        "Save"
      ),
      h(
        "button.tags-page__reset",
        {
          attrs: { disabled: !dirty },
          on: { click: () => dispatch({ type: "RegistryReset" }) },
        },
        "Reset"
      ),
    ]),
  ]);
}

// A hardcoded system tag: name + type + how it's set, no controls.
function viewSystemTagRow(tag: SystemTag): VNode {
  const note = tag.assignable ? "set on cards" : "auto";
  return h("div.tag-row.tag-row--system", [
    h("div.tag-row__main", [
      h("span.tag-row__name", tag.name),
      h("span.tag-row__system-note", note),
      h("span.tag-row__type-label", TYPE_LABELS[tag.valueType]),
    ]),
  ]);
}

function viewTagRow(
  def: TagDef,
  index: number,
  savedNames: Set<string>,
  dispatch: (msg: Msg) => void
): VNode {
  const isExisting = savedNames.has(def.name);
  const hasValues = def.valueType !== "none";

  const nameNode = isExisting
    ? h("span.tag-row__name", def.name)
    : h("input.tag-row__name-input", {
        attrs: { value: def.name, placeholder: "tag name" },
        props: { value: def.name },
        on: {
          input: (e: Event) =>
            dispatch({ type: "RegistrySetName", index, name: (e.target as HTMLInputElement).value }),
        },
      });

  return h("div.tag-row", [
    h("div.tag-row__main", [
      nameNode,
      h(
        "select.tag-row__type",
        {
          on: {
            change: (e: Event) =>
              dispatch({
                type: "RegistrySetType",
                index,
                valueType: (e.target as HTMLSelectElement).value as TagValueType,
              }),
          },
        },
        ALL_TYPES.map((t) =>
          h("option", { attrs: { value: t, selected: t === def.valueType } }, TYPE_LABELS[t])
        )
      ),
      h(
        "button.tag-row__delete",
        {
          attrs: { title: "Delete tag" },
          on: { click: () => dispatch({ type: "RegistryDeleteTag", index }) },
        },
        "🗑"
      ),
    ]),
    ...(hasValues ? [viewValuesEditor(def, index, dispatch)] : []),
  ]);
}

function viewValuesEditor(def: TagDef, index: number, dispatch: (msg: Msg) => void): VNode {
  return h("div.tag-row__values", [
    h(
      "div.tag-row__chips",
      def.values.map((v, vi) =>
        h("span.chip.tag-chip", [
          formatValue(v),
          h(
            "span.tag-chip__x",
            { on: { click: () => dispatch({ type: "RegistryRemoveValue", index, valueIndex: vi }) } },
            "×"
          ),
        ])
      )
    ),
    h("input.tag-row__add-value", {
      attrs: { placeholder: "add value…" },
      on: {
        keydown: (e: KeyboardEvent) => {
          if (e.key !== "Enter") return;
          const el = e.target as HTMLInputElement;
          dispatch({ type: "RegistryAddValue", index, value: el.value });
          el.value = "";
        },
      },
    }),
    h("label.tag-row__allow", [
      h("input", {
        attrs: {
          type: "checkbox",
          checked: def.allowAll,
          // Closed enum requires a non-empty list, so disable until one exists.
          disabled: def.values.length === 0,
        },
        on: {
          change: (e: Event) =>
            dispatch({
              type: "RegistrySetAllowAll",
              index,
              allowAll: (e.target as HTMLInputElement).checked,
            }),
        },
      }),
      " allow any value",
    ]),
  ]);
}
